import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  DollarSign,
  Users,
  Server,
  HardDrive,
  Globe,
  Bot,
  Search,
  Shield,
  Headphones,
  Eye,
  Heart,
  Code,
  TrendingUp,
  CheckCircle,
  ArrowRight,
  Building2,
  Rocket
} from 'lucide-react';

const CostRoadmap = () => {
  const [selectedScale, setSelectedScale] = useState('100K');

  const scaleData = {
    '10K': {
      users: '10,000',
      label: 'Early Stage',
      color: 'bg-green-500',
      infra: { compute: 50, database: 50, storage: 20, cdn: 20, ai: 10, search: 0, other: 20, total: 170 },
      labor: { support: 0, moderation: 0, community: 0, devops: 0, trustSafety: 0, total: 0 },
      staff: '1 (Founder)',
      perUser: 0.005
    },
    '100K': {
      users: '100,000',
      label: 'Growth',
      color: 'bg-blue-500',
      infra: { compute: 50, database: 50, storage: 30, cdn: 20, ai: 20, search: 0, other: 30, total: 200 },
      labor: { support: 2000, moderation: 1200, community: 2500, devops: 4000, trustSafety: 0, total: 9700 },
      staff: '3-4',
      perUser: 0.10
    },
    '500K': {
      users: '500,000',
      label: 'Scale',
      color: 'bg-purple-500',
      infra: { compute: 150, database: 200, storage: 150, cdn: 200, ai: 150, search: 100, other: 50, total: 1000 },
      labor: { support: 12000, moderation: 8000, community: 7500, devops: 10000, trustSafety: 6500, total: 44000 },
      staff: '8-12',
      perUser: 0.09
    },
    '1M': {
      users: '1,000,000',
      label: 'Mainstream',
      color: 'bg-amber-500',
      infra: { compute: 300, database: 500, storage: 300, cdn: 200, ai: 300, search: 200, other: 200, total: 2000 },
      labor: { support: 35000, moderation: 20000, community: 14000, devops: 23000, trustSafety: 16000, total: 108000 },
      staff: '18-25',
      perUser: 0.11
    },
    '10M': {
      users: '10,000,000',
      label: 'Enterprise',
      color: 'bg-red-500',
      infra: { compute: 2000, database: 5000, storage: 3000, cdn: 500, ai: 3000, search: 1000, other: 1500, total: 16000 },
      labor: { support: 300000, moderation: 185000, community: 70000, devops: 75000, trustSafety: 65000, total: 695000 },
      staff: '130-180',
      perUser: 0.07
    }
  };

  const current = scaleData[selectedScale];
  const totalMonthly = current.infra.total + current.labor.total;

  const formatCurrency = (amount) => {
    if (amount >= 1000) {
      return `$${(amount / 1000).toFixed(0)}K`;
    }
    return `$${amount}`;
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-bark-50 to-white">
      {/* Hero Section */}
      <section className="py-16 px-4">
        <div className="container mx-auto max-w-6xl text-center">
          <Badge className="mb-4 bg-primary/10 text-primary">For Supporters & Investors</Badge>
          <h1 className="text-4xl md:text-5xl font-heading font-bold mb-4">
            Cost & Growth Roadmap
          </h1>
          <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
            Transparent breakdown of infrastructure and operational costs as AADC scales 
            to serve millions of pet lovers worldwide.
          </p>
        </div>
      </section>

      {/* Scale Selector */}
      <section className="px-4 pb-8">
        <div className="container mx-auto max-w-6xl">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Users className="w-5 h-5" />
                Select User Scale
              </CardTitle>
              <CardDescription>
                Click to see detailed cost breakdown at each growth stage
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-5 gap-2">
                {Object.entries(scaleData).map(([key, data]) => (
                  <button
                    key={key}
                    onClick={() => setSelectedScale(key)}
                    className={`p-4 rounded-lg border-2 transition-all ${
                      selectedScale === key 
                        ? 'border-primary bg-primary/5' 
                        : 'border-transparent bg-muted hover:bg-muted/80'
                    }`}
                  >
                    <div className={`w-3 h-3 rounded-full ${data.color} mx-auto mb-2`} />
                    <div className="font-bold">{data.users}</div>
                    <div className="text-xs text-muted-foreground">{data.label}</div>
                  </button>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Cost Summary Cards */}
      <section className="px-4 pb-8">
        <div className="container mx-auto max-w-6xl">
          <div className="grid md:grid-cols-4 gap-4">
            <Card className="bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-blue-600 font-medium">Infrastructure</p>
                    <p className="text-3xl font-bold text-blue-700">
                      {formatCurrency(current.infra.total)}
                    </p>
                    <p className="text-xs text-blue-500">per month</p>
                  </div>
                  <Server className="w-10 h-10 text-blue-400" />
                </div>
              </CardContent>
            </Card>

            <Card className="bg-gradient-to-br from-purple-50 to-purple-100 border-purple-200">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-purple-600 font-medium">Labor & Staff</p>
                    <p className="text-3xl font-bold text-purple-700">
                      {formatCurrency(current.labor.total)}
                    </p>
                    <p className="text-xs text-purple-500">{current.staff} people</p>
                  </div>
                  <Headphones className="w-10 h-10 text-purple-400" />
                </div>
              </CardContent>
            </Card>

            <Card className="bg-gradient-to-br from-amber-50 to-amber-100 border-amber-200">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-amber-600 font-medium">Total Monthly</p>
                    <p className="text-3xl font-bold text-amber-700">
                      {formatCurrency(totalMonthly)}
                    </p>
                    <p className="text-xs text-amber-500">operating cost</p>
                  </div>
                  <DollarSign className="w-10 h-10 text-amber-400" />
                </div>
              </CardContent>
            </Card>

            <Card className="bg-gradient-to-br from-green-50 to-green-100 border-green-200">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-green-600 font-medium">Cost Per User</p>
                    <p className="text-3xl font-bold text-green-700">
                      ${current.perUser.toFixed(2)}
                    </p>
                    <p className="text-xs text-green-500">per month</p>
                  </div>
                  <TrendingUp className="w-10 h-10 text-green-400" />
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Detailed Breakdown */}
      <section className="px-4 pb-8">
        <div className="container mx-auto max-w-6xl">
          <Tabs defaultValue="infrastructure" className="space-y-6">
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="infrastructure">
                <Server className="w-4 h-4 mr-2" />
                Infrastructure Costs
              </TabsTrigger>
              <TabsTrigger value="labor">
                <Users className="w-4 h-4 mr-2" />
                Labor Costs
              </TabsTrigger>
            </TabsList>

            <TabsContent value="infrastructure">
              <Card>
                <CardHeader>
                  <CardTitle>Infrastructure Breakdown at {current.users} Users</CardTitle>
                  <CardDescription>
                    Monthly costs for servers, databases, storage, and services
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {[
                      { label: 'Compute (Servers)', value: current.infra.compute, icon: Server, color: 'bg-blue-500' },
                      { label: 'Database (MongoDB)', value: current.infra.database, icon: HardDrive, color: 'bg-green-500' },
                      { label: 'File Storage', value: current.infra.storage, icon: HardDrive, color: 'bg-purple-500' },
                      { label: 'CDN & Bandwidth', value: current.infra.cdn, icon: Globe, color: 'bg-amber-500' },
                      { label: 'AI/LLM Services', value: current.infra.ai, icon: Bot, color: 'bg-pink-500' },
                      { label: 'Search (Elasticsearch)', value: current.infra.search, icon: Search, color: 'bg-cyan-500' },
                      { label: 'Other (Monitoring, etc.)', value: current.infra.other, icon: Shield, color: 'bg-gray-500' },
                    ].map((item, idx) => (
                      <div key={idx} className="flex items-center gap-4">
                        <div className={`w-10 h-10 rounded-lg ${item.color} flex items-center justify-center`}>
                          <item.icon className="w-5 h-5 text-white" />
                        </div>
                        <div className="flex-1">
                          <div className="flex justify-between items-center mb-1">
                            <span className="font-medium">{item.label}</span>
                            <span className="font-bold">${item.value.toLocaleString()}/mo</span>
                          </div>
                          <div className="h-2 bg-muted rounded-full overflow-hidden">
                            <div 
                              className={`h-full ${item.color}`}
                              style={{ width: `${(item.value / current.infra.total) * 100}%` }}
                            />
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="labor">
              <Card>
                <CardHeader>
                  <CardTitle>Labor Breakdown at {current.users} Users</CardTitle>
                  <CardDescription>
                    Monthly costs for support, moderation, and operations staff
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {current.labor.total === 0 ? (
                    <div className="text-center py-8 text-muted-foreground">
                      <Heart className="w-12 h-12 mx-auto mb-4 opacity-50" />
                      <p>At this stage, the founder handles all operations</p>
                      <p className="text-sm">No additional labor costs</p>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {[
                        { label: 'User Support', value: current.labor.support, icon: Headphones, color: 'bg-blue-500', desc: 'Tickets, inquiries, help' },
                        { label: 'Content Moderation', value: current.labor.moderation, icon: Eye, color: 'bg-red-500', desc: 'Reviewing flagged content' },
                        { label: 'Community Management', value: current.labor.community, icon: Heart, color: 'bg-pink-500', desc: 'Engagement, events, social' },
                        { label: 'DevOps/Engineering', value: current.labor.devops, icon: Code, color: 'bg-green-500', desc: 'Infrastructure, on-call' },
                        { label: 'Trust & Safety', value: current.labor.trustSafety, icon: Shield, color: 'bg-purple-500', desc: 'Fraud, legal, compliance' },
                      ].map((item, idx) => (
                        <div key={idx} className="flex items-center gap-4">
                          <div className={`w-10 h-10 rounded-lg ${item.color} flex items-center justify-center`}>
                            <item.icon className="w-5 h-5 text-white" />
                          </div>
                          <div className="flex-1">
                            <div className="flex justify-between items-center mb-1">
                              <div>
                                <span className="font-medium">{item.label}</span>
                                <span className="text-xs text-muted-foreground ml-2">{item.desc}</span>
                              </div>
                              <span className="font-bold">${item.value.toLocaleString()}/mo</span>
                            </div>
                            <div className="h-2 bg-muted rounded-full overflow-hidden">
                              <div 
                                className={`h-full ${item.color}`}
                                style={{ width: `${(item.value / current.labor.total) * 100}%` }}
                              />
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </div>
      </section>

      {/* Growth Timeline */}
      <section className="px-4 pb-8">
        <div className="container mx-auto max-w-6xl">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Rocket className="w-5 h-5" />
                Scaling Roadmap
              </CardTitle>
              <CardDescription>
                Key infrastructure upgrades at each growth milestone
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-6">
                {[
                  {
                    scale: '0 - 100K',
                    title: 'Foundation',
                    cost: '$200/mo',
                    items: ['Database indexes (done)', 'Connection pooling (done)', 'Basic monitoring', 'Single server deployment']
                  },
                  {
                    scale: '500K',
                    title: 'Growth Infrastructure',
                    cost: '$1,000/mo',
                    items: ['Redis caching layer', 'CDN for static assets', 'Rate limiting', 'Image compression']
                  },
                  {
                    scale: '1M',
                    title: 'Scale Infrastructure',
                    cost: '$2,000/mo',
                    items: ['Database read replicas', 'Load balancer + 3 app servers', 'Elasticsearch for search', 'Message queue for async jobs', 'S3 for file storage']
                  },
                  {
                    scale: '10M',
                    title: 'Enterprise Infrastructure',
                    cost: '$16,000/mo',
                    items: ['Database sharding', 'Multi-region deployment', 'Akamai CDN + security', 'WebSocket real-time', 'Microservices architecture', 'ML recommendations']
                  }
                ].map((phase, idx) => (
                  <div key={idx} className="flex gap-4">
                    <div className="flex flex-col items-center">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                        idx === 0 ? 'bg-green-500' : 'bg-muted'
                      } text-white font-bold text-sm`}>
                        {idx === 0 ? <CheckCircle className="w-5 h-5" /> : idx + 1}
                      </div>
                      {idx < 3 && <div className="w-0.5 h-full bg-muted mt-2" />}
                    </div>
                    <div className="flex-1 pb-6">
                      <div className="flex items-center gap-2 mb-2">
                        <Badge variant="outline">{phase.scale} users</Badge>
                        <span className="font-bold">{phase.title}</span>
                        <span className="text-sm text-muted-foreground">({phase.cost} infra)</span>
                      </div>
                      <ul className="grid md:grid-cols-2 gap-1">
                        {phase.items.map((item, i) => (
                          <li key={i} className="flex items-center gap-2 text-sm text-muted-foreground">
                            <ArrowRight className="w-3 h-3" />
                            {item}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Investment Tiers */}
      <section className="px-4 pb-16">
        <div className="container mx-auto max-w-6xl">
          <Card className="bg-gradient-to-br from-primary/5 to-primary/10 border-primary/20">
            <CardHeader className="text-center">
              <CardTitle className="flex items-center justify-center gap-2">
                <Building2 className="w-5 h-5" />
                Support AADC
              </CardTitle>
              <CardDescription>
                Help us build the premier community for pet lovers
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid md:grid-cols-3 gap-6">
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">Seed Supporter</CardTitle>
                    <CardDescription>Early believer</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="text-3xl font-bold mb-4">$5,000+</div>
                    <ul className="space-y-2 text-sm">
                      <li className="flex items-center gap-2">
                        <CheckCircle className="w-4 h-4 text-green-500" />
                        Founding supporter recognition
                      </li>
                      <li className="flex items-center gap-2">
                        <CheckCircle className="w-4 h-4 text-green-500" />
                        Lifetime premium membership
                      </li>
                      <li className="flex items-center gap-2">
                        <CheckCircle className="w-4 h-4 text-green-500" />
                        Quarterly updates
                      </li>
                    </ul>
                  </CardContent>
                </Card>

                <Card className="border-primary">
                  <CardHeader>
                    <Badge className="w-fit mb-2">Most Popular</Badge>
                    <CardTitle className="text-lg">Growth Partner</CardTitle>
                    <CardDescription>Strategic supporter</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="text-3xl font-bold mb-4">$25,000+</div>
                    <ul className="space-y-2 text-sm">
                      <li className="flex items-center gap-2">
                        <CheckCircle className="w-4 h-4 text-green-500" />
                        All Seed benefits
                      </li>
                      <li className="flex items-center gap-2">
                        <CheckCircle className="w-4 h-4 text-green-500" />
                        Advisory board seat
                      </li>
                      <li className="flex items-center gap-2">
                        <CheckCircle className="w-4 h-4 text-green-500" />
                        Feature naming rights
                      </li>
                      <li className="flex items-center gap-2">
                        <CheckCircle className="w-4 h-4 text-green-500" />
                        Monthly strategy calls
                      </li>
                    </ul>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">Founding Investor</CardTitle>
                    <CardDescription>Equity partnership</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="text-3xl font-bold mb-4">$100,000+</div>
                    <ul className="space-y-2 text-sm">
                      <li className="flex items-center gap-2">
                        <CheckCircle className="w-4 h-4 text-green-500" />
                        All Growth benefits
                      </li>
                      <li className="flex items-center gap-2">
                        <CheckCircle className="w-4 h-4 text-green-500" />
                        Equity stake (negotiable)
                      </li>
                      <li className="flex items-center gap-2">
                        <CheckCircle className="w-4 h-4 text-green-500" />
                        Board observer rights
                      </li>
                      <li className="flex items-center gap-2">
                        <CheckCircle className="w-4 h-4 text-green-500" />
                        Co-branding opportunities
                      </li>
                    </ul>
                  </CardContent>
                </Card>
              </div>

              <div className="text-center mt-8">
                <p className="text-muted-foreground mb-4">
                  Interested in supporting AADC? Let's talk.
                </p>
                <a 
                  href="mailto:invest@pawsitively.com" 
                  className="inline-flex items-center gap-2 bg-primary text-primary-foreground px-6 py-3 rounded-lg font-medium hover:bg-primary/90 transition-colors"
                >
                  Contact Us
                  <ArrowRight className="w-4 h-4" />
                </a>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>
    </div>
  );
};

export default CostRoadmap;
